import React, { useCallback, useEffect, useState } from "react";
import {
  getRecognitionFeedbackConfig,
  updateRecognitionFeedbackConfig
} from "../../../../api/PreformanceManagement/Feedback";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Button from "../../../../components/ui/Button";
import { Spinner } from "../../../../components/ui/Spinner";
import { Switch } from "../../../../components/ui/Switch";
import { PageLayout } from "../../../../components/ui/PageLayout";
import { Lock } from "lucide-react";
import { useToast, ToastRenderer } from "../../../../hooks/useToast";
import { Roles as AuthRoles } from '../../../../components/roles';

const RecognitionPrivacy: React.FC = () => {
  const { t } = useTranslation();
  const company = useSelector((state: any) => state.user.company);
  const role = useSelector((state: any) => state.user.role);
  const { toast, showToast, dismissToast } = useToast();

  const [allowPublicRecognitionFeedback, setAllowPublicRecognitionFeedback] = useState(false);
  const [loading, setLoading] = useState(false);

  const companyId = company?.id;
  const isAuthorized = AuthRoles.HRAndADMIN.includes(role ?? "");

  const fetchConfig = useCallback(async () => {
    console.log("User role from Redux:", role);
    console.log("AuthRoles.ADMIN:", AuthRoles.ADMIN);
    console.log("Is authorized:", isAuthorized);
    if (!companyId) return;
    setLoading(true);
    try {
      const result = await getRecognitionFeedbackConfig();
      console.log("Fetch configuration result: " , result);
      if (result.payload) {
        console.log("result was success and contains data...");
        setAllowPublicRecognitionFeedback(result.payload.allowPublicRecognitionFeedback);
      } else if (result.errors && result.errors.length > 0) {
        showToast(result.errors[0].description || t('Failed to load configuration'), 'error');
      } else {
        showToast(t('Failed to load configuration'), 'error');
      }
    } catch (e: any) {
      showToast(t('Failed to load configuration'), 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [companyId, t, showToast]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const submit = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      console.log("This is the const submit, in the try...");
      console.log("Submitting config:", { allowPublicRecognitionFeedback });
      const result = await updateRecognitionFeedbackConfig({
        allowPublicRecognitionFeedback
      });
      console.log("Update result:", result);
      if (result.payload) {
        console.log("Update successful!");
        showToast(t('Settings saved successfully'), 'success');
        // Update local state with the response
        setAllowPublicRecognitionFeedback(result.payload.allowPublicRecognitionFeedback);
      } else if (result.errors && result.errors.length > 0) {
        console.log("Update failed with errors:", result.errors);
        showToast(result.errors[0].description || t('Update failed'), 'error');
      } else {
        console.log("Update failed - unknown error");
        showToast(t('Update failed'), 'error');
      }
    } catch (e: any) {
      console.error(e);
      showToast(t('Update failed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="p-8">
        {/* TODO(batch5): Replace with shared Alert component */}
        <div className="flex items-center gap-2 rounded-md border border-warning bg-warning/10 px-4 py-3 text-sm text-warning-foreground">
          {t('You do not have permission to access this page')}
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      title={t('Recognition & Feedback Privacy Settings')}
      description={t('Configure privacy options for recognition and feedback features')}
      icon={Lock}
      accentColor="rose"
    >
      <div className="flex flex-col">
      <div className="flex flex-col gap-6">
        {loading && !allowPublicRecognitionFeedback && (
          <div className="flex justify-center p-8">
            <Spinner size="lg" />
          </div>
        )}

        {!loading && (
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-start gap-4">
              <Switch
                checked={allowPublicRecognitionFeedback}
                onCheckedChange={(checked) => setAllowPublicRecognitionFeedback(checked)}
                disabled={loading}
              />
              <div className="flex flex-col gap-1">
                <span className="text-base font-semibold text-foreground">
                  {t('Allow Public Recognition & Feedback')}
                </span>
                <span className="text-sm text-muted-foreground">
                  {allowPublicRecognitionFeedback
                    ? t('Employees can choose to make their recognition and feedback visible to all company members')
                    : t('All recognition and feedback will be private (visible only to sender and recipient)')}
                </span>
              </div>
            </div>

            <div className="mt-4">
              {/* TODO(batch5): Replace with shared Alert component */}
              <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground">
                {allowPublicRecognitionFeedback
                  ? t('When enabled, employees will see a visibility option when creating recognition or feedback. They can choose to make it public (visible to all) or private (only visible to sender and recipient).')
                  : t('When disabled, all existing public recognition and feedback will automatically become private, and the visibility option will be hidden from employees.')}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={fetchConfig}
            disabled={loading}
          >
            {t('Reset')}
          </Button>
          <Button
            onClick={submit}
            disabled={loading}
            loading={loading}
          >
            {t('Save Changes')}
          </Button>
        </div>
      </div>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
};

export default RecognitionPrivacy;
